<template>
  <div v-show="showThisCell">
    <div
      class="canvas-wrapper"
      :class="{
        unclassified: unclassifiedState,
        selected: selectedState,
        'canvas-wrapper--measurement-ruler-overflow': measurementRulerOverflows,
      }"
      :style="{ width: `${thisImgWidth}px`, height: `${thisImgWidth}px` }"
      :tabindex="measurementSurfaceTabIndex"
      :aria-label="measurementSurfaceTabIndex === 0 ? `Cell #${tileId} measurement surface` : undefined"
      @pointerenter="activateRulerSurface"
      @focusin="activateRulerSurface"
      @click="changeMark"
      @pointercancel="clearTouchClickSuppression"
      @contextmenu.prevent.stop="handleRightClick"
      @mouseover="handleMouseOver"
      @mouseout="handleMouseOut"
    >
      <img
        ref="imgElement"
        :src="imgSrc"
        :style="{ width: `${thisImgWidth}px`, height: `${thisImgWidth}px` }"
        :alt="'Failed to load image'"
        :blank-height="153.6"
        decoding="async"
        loading="lazy"
        @load="handleImageLoad"
        @error="imgError"
      >

      <div
        v-if="shouldRenderStage"
        ref="konvaRoot"
        class="canvas-overlay"
        :style="{ width: thisImgWidth + 'px', height: thisImgWidth + 'px' }"
      />
      <MeasurementRuler
        v-if="measurementLayout"
        kind="wbc-lpc"
        :task-id="taskId"
        :surface-id="rulerSurfaceId"
        :image-id="`Cell #${tileId}`"
        :surface-active="showThisCell && isMeasurementSurfaceIntersecting"
        :effective-mpp-x="effectiveMppX"
        :effective-mpp-y="effectiveMppY"
        :natural-width="measurementNaturalWidth"
        :natural-height="measurementNaturalHeight"
        :rendered-width="thisImgWidth"
        :rendered-height="thisImgWidth"
        @interaction-end="clearTouchClickSuppression"
      />
    </div>
    <div class="tile-info">
      #{{ tileId }} {{ localCellTag }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import Konva from 'konva';
import { useConfigStore } from '@/store/config';
import { useCellGroupStore, useMeasurementRulerStore } from '@/store';
import type { CellBoundingBox } from '@/types/api';
import type { CheckboxValue } from 'bootstrap-vue-next';
import { useApi, useConstants } from "@/composables/useAppGlobals";
import { useCellVisibility } from '@/composables/useCellVisibility';
import { useIntersectionHandler } from '@/composables/useIntersectionHandler';
import { useCellImageCache } from '@/composables/useCellImageCache';
import MeasurementRuler from '@/components/measurement/MeasurementRuler.vue';
import {
  calculateRulerLayout,
  resolveEffectiveMpp,
} from '@/utils/measurementRuler';
import { matchesCellIdentity } from '@/utils/store';

const props = withDefaults(defineProps<{
  isZPressing?: boolean;
  tileId: number;
  cellType: string;
  cellTag?: string;
  imgPath?: string;
  imgWidth: number;
  bbox?: number[][];
  showBbox?: boolean | CheckboxValue | CheckboxValue[];
  drawingMode?: boolean | CheckboxValue | CheckboxValue[];
  showNormal?: boolean | CheckboxValue | CheckboxValue[];
  showWrong?: boolean | CheckboxValue | CheckboxValue[];
  showExcluded?: boolean | CheckboxValue | CheckboxValue[];
  taskId?: string;
  effectiveMppX?: number | null;
  effectiveMppY?: number | null;
}>(), {
  isZPressing: false,
  cellTag: '',
  imgPath: '',
  bbox: () => [],
  showBbox: false,
  drawingMode: false,
  showNormal: true,
  showWrong: true,
  showExcluded: true,
  taskId: '',
  effectiveMppX: null,
  effectiveMppY: null,
});

const emit = defineEmits(['activate', 'cellRightClick']);

const $api = useApi();
const $const = useConstants();
const config = useConfigStore();
const cellGroups = useCellGroupStore();
const rulerStore = useMeasurementRulerStore();
const { getCachedUrl } = useCellImageCache();

const CELL_DEFAULT_SIZE = $const?.CELL_DEFAULT_SIZE ?? 153.6;
const DEFAULT_CELL_IMG_SIZE = $const?.DEFAULT_CELL_IMG_SIZE ?? CELL_DEFAULT_SIZE;
const UNCLASSIFIED_TAG = $const?.CELL_TAGS?.UNCLASSIFIED ?? 'UNCLASSIFIED';
const SELECTED_TAG = $const?.CELL_TAGS?.SELECTED ?? 'SELECTED';

function isReviewState(state?: string): boolean {
  if (!$const || !state) return false;
  return state === $const.TASK_STATES.RELABELING || state === $const.TASK_STATES.FINAL_REVIEW;
}

type NormalizedBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type BBox = [number, number, number, number];

type StageRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type RectDrawable = StageRect & {
  stroke: string;
  strokeWidth: number;
  id?: string;
};

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function normalizeBox(points: BBox | undefined): NormalizedBox | null {
  if (!Array.isArray(points) || points.length < 4) return null;
  const [x1, y1, x2, y2] = points;
  const minX = Math.min(x1, x2);
  const minY = Math.min(y1, y2);
  const maxX = Math.max(x1, x2);
  const maxY = Math.max(y1, y2);
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }
  if (maxX === minX || maxY === minY) {
    return null;
  }
  return { minX, minY, maxX, maxY };
}

function stageRectFromRaw(
  points: BBox | undefined,
  scaleX: number,
  scaleY: number,
): StageRect | null {
  const normalized = normalizeBox(points);
  if (
    !normalized
    || !Number.isFinite(scaleX)
    || !Number.isFinite(scaleY)
    || scaleX <= 0
    || scaleY <= 0
  ) return null;
  return {
    x: normalized.minX * scaleX,
    y: normalized.minY * scaleY,
    width: (normalized.maxX - normalized.minX) * scaleX,
    height: (normalized.maxY - normalized.minY) * scaleY,
  };
}

function rawBoxFromStage(
  points: BBox | undefined,
  scaleX: number,
  scaleY: number,
  naturalWidth: number,
  naturalHeight: number,
): BBox | null {
  const normalized = normalizeBox(points);
  if (
    !normalized
    || !Number.isFinite(scaleX)
    || !Number.isFinite(scaleY)
    || !Number.isFinite(naturalWidth)
    || !Number.isFinite(naturalHeight)
    || scaleX <= 0
    || scaleY <= 0
    || naturalWidth <= 0
    || naturalHeight <= 0
  ) return null;
  const minX = clamp(normalized.minX / scaleX, 0, naturalWidth);
  const minY = clamp(normalized.minY / scaleY, 0, naturalHeight);
  const maxX = clamp(normalized.maxX / scaleX, 0, naturalWidth);
  const maxY = clamp(normalized.maxY / scaleY, 0, naturalHeight);
  if (maxX === minX || maxY === minY) return null;
  return [minX, minY, maxX, maxY];
}

const isMouseOver = ref(false);
const isMarked = ref(false);
const imgElement = ref<HTMLImageElement | null>(null);
const imgSrc = ref('');
const localCellTag = ref('');
const originCellTag = ref('');
const isDragging = ref(false);
const customBbox = ref<BBox | []>([]);
// removed unused labelChecked
const baseImgWidth = ref<number>(CELL_DEFAULT_SIZE);
const loadRetry = ref(0);
const widthUpdateFrame = ref<number | null>(null);
const measurementNaturalWidth = ref(0);
const measurementNaturalHeight = ref(0);
const isMeasurementSurfaceIntersecting = ref(false);
const displayedImagePath = ref('');
const pendingImagePath = ref('');
let stopIntersectionObserver: (() => void) | null = null;
let cachedImgPath: string | undefined = undefined;
let imgRequestId = 0;
let suppressNextTouchClick = false;

async function loadImageForPath(
  path: string,
  options: { resetRawSize: boolean },
): Promise<number> {
  cachedImgPath = path;
  const requestId = ++imgRequestId;
  const rawUrl = $api.getCellUrls(path);
  if (options.resetRawSize) {
    rawImgSize.value = CELL_DEFAULT_SIZE;
  }
  try {
    const cachedUrl = await getCachedUrl(rawUrl);
    if (requestId === imgRequestId) {
      pendingImagePath.value = path;
      imgSrc.value = cachedUrl;
    }
  } catch {
    if (requestId === imgRequestId) {
      pendingImagePath.value = path;
      imgSrc.value = rawUrl;
    }
  }
  return requestId;
}

// Use the visibility composable for cleaner observer handling
const { isVisible, rawImgSize, handleVisibilityChange } = useCellVisibility(
  imgElement,
  CELL_DEFAULT_SIZE
);

const konvaRoot = ref<HTMLDivElement | null>(null);
let stage: Konva.Stage | null = null;
let layer: Konva.Layer | null = null;

const showThisCell = computed(() => {
  const cellTag = localCellTag.value || effectiveCellTag.value || '';
  if (cellTag === UNCLASSIFIED_TAG && props.showWrong) {
    return true;
  }
  if (cellTag === '' && props.showNormal) {
    return true;
  }
  if (cellTag === SELECTED_TAG) {
    return true;
  }
  return false;
});

const unclassifiedState = computed(() => localCellTag.value === UNCLASSIFIED_TAG);
const selectedState = computed(() => localCellTag.value === SELECTED_TAG);
const rulerSurfaceId = computed(() => `lpc:${props.tileId}:${props.imgPath}`);

// Prefer canonical data from the Pinia store when available. This lets the
// component react to updates saved to the store without requiring parents to
// re-propagate props.
const storeCell = computed(() => {
  const arr = (cellGroups.groupDict?.[props.cellType] || []) as CellBoundingBox[];
  return arr.find((cell) => matchesCellIdentity(cell, props.tileId, props.imgPath)) ?? null;
});

const effectiveBBoxes = computed(() => {
  const obj = storeCell.value as CellBoundingBox;
  if (obj?.bounding_boxes && Array.isArray(obj.bounding_boxes)) {
    return obj.bounding_boxes;
  }
  return Array.isArray(props.bbox) ? props.bbox : [];
});

const effectiveCellTag = computed(() => {
  const tag = (storeCell.value as CellBoundingBox)?.tag;
  return tag !== undefined ? String(tag) : (props.cellTag ?? '');
});

const hasDraftBbox = computed(() => customBbox.value.length >= 4);

const allRectConfigs = computed<RectDrawable[]>(() => {
  if (!props.showBbox || (props.drawingMode && hasDraftBbox.value)) {
    return [];
  }
  if (!Array.isArray(effectiveBBoxes.value) || effectiveBBoxes.value.length === 0) {
    return [];
  }
  return effectiveBBoxes.value.reduce((rects: RectDrawable[], bbox, index) => {
    const rect = stageRectFromRaw(bbox as BBox, bboxScaleX.value, bboxScaleY.value);
    if (rect) {
      rects.push({
        id: `bbox-${index}`,
        ...rect,
        stroke: "red",
        strokeWidth: 2,
      });
    }
    return rects;
  }, []);
});

const customRectConfig = computed<RectDrawable | null>(() => {
  if (!hasDraftBbox.value) return null;

  const rect = stageRectFromRaw(
    customBbox.value as BBox,
    bboxScaleX.value,
    bboxScaleY.value,
  );
  
  return rect ? {
    ...rect,
    stroke: "green",
    strokeWidth: 2,
  } : null;
});

const shouldRenderStage = computed(() => {
  const hasContent = !!props.showBbox || !!props.drawingMode || hasDraftBbox.value;
  const shouldShow = isVisible.value || !!props.drawingMode || hasDraftBbox.value;
  return hasContent && shouldShow;
});

const thisImgWidth = computed({
  get: () =>
    props.isZPressing && isMouseOver.value ? DEFAULT_CELL_IMG_SIZE * 1.5 : baseImgWidth.value,
  set: (value: number) => {
    if (Number.isFinite(value)) {
      baseImgWidth.value = value;
    }
  },
});
const bboxNaturalWidth = computed(() => (
  measurementNaturalWidth.value > 0 ? measurementNaturalWidth.value : rawImgSize.value
));
const bboxNaturalHeight = computed(() => (
  measurementNaturalHeight.value > 0 ? measurementNaturalHeight.value : rawImgSize.value
));
const bboxScaleX = computed(() => thisImgWidth.value / bboxNaturalWidth.value);
const bboxScaleY = computed(() => thisImgWidth.value / bboxNaturalHeight.value);
const measurementSurfaceReady = computed(() => (
  Boolean(props.taskId)
  && resolveEffectiveMpp(props.effectiveMppX, props.effectiveMppY) !== null
  && displayedImagePath.value === props.imgPath
  && measurementNaturalWidth.value > 0
  && measurementNaturalHeight.value > 0
));
const measurementLayout = computed(() => (
  measurementSurfaceReady.value
    ? calculateRulerLayout({
      kind: "wbc-lpc",
      effectiveMppX: props.effectiveMppX,
      effectiveMppY: props.effectiveMppY,
      naturalWidth: measurementNaturalWidth.value,
      naturalHeight: measurementNaturalHeight.value,
      renderedWidth: thisImgWidth.value,
      renderedHeight: thisImgWidth.value,
      position: rulerStore.position(props.taskId, rulerSurfaceId.value),
    })
    : null
));
const measurementRulerOverflows = computed(() => {
  const layout = measurementLayout.value;
  return Boolean(
    layout
    && (layout.overflowsX || layout.overflowsY)
    && showThisCell.value
    && isMeasurementSurfaceIntersecting.value
    && rulerStore.isVisible(props.taskId)
    && rulerStore.isActiveSurface(props.taskId, rulerSurfaceId.value)
  );
});
const measurementSurfaceTabIndex = computed(() => (
  measurementLayout.value !== null
  && isMeasurementSurfaceIntersecting.value
  && rulerStore.isVisible(props.taskId)
    ? 0
    : undefined
));

function getCurrentSlideState(): string | undefined {
  const stateKey = $const?.SLIDE_KEYS?.STATE;
  if (!stateKey) return undefined;
  const value = config.slideInfo?.[stateKey];
  return typeof value === 'string' ? value : undefined;
}

function refreshDrawing() {
  if (!shouldRenderStage.value) {
    return;
  }
  // Ensure DOM updates are flushed before manipulating Konva
  nextTick(() => {
    if (!konvaRoot.value) return;
    if (!stage) {
      createStage();
      return;
    }
    syncStageDimensions();
    draw();
  });
}

watch(() => props.imgWidth, (width: number | undefined) => {
  if (width == null) return;
  if (widthUpdateFrame.value) {
    cancelAnimationFrame(widthUpdateFrame.value);
  }
  widthUpdateFrame.value = requestAnimationFrame(() => {
    baseImgWidth.value = width;
    widthUpdateFrame.value = null;
    refreshDrawing();
  });
});

watch(() => props.drawingMode, 
  (enabled: boolean | CheckboxValue | CheckboxValue[] | undefined) => {
    // `enabled` can be boolean or bootstrap checkbox values; coerce to boolean for checks
    const isEnabled = Array.isArray(enabled) ? enabled.length > 0 : Boolean(enabled);
    if (!isEnabled && hasDraftBbox.value) {
      customBbox.value = [];
      refreshDrawing();
    }
  }
);

watch(() => props.imgPath, async (path: string | undefined) => {
  // Skip if path is same as cached or empty
  if (!path || path === cachedImgPath) return;

  measurementNaturalWidth.value = 0;
  measurementNaturalHeight.value = 0;
  displayedImagePath.value = '';
  pendingImagePath.value = '';
  imgSrc.value = '';
  const requestId = await loadImageForPath(path, { resetRawSize: true });
  if (requestId !== imgRequestId) return;
  void getRawImgSize(requestId, imgSrc.value);
});

watch(rawImgSize, () => {
  config.setIsBusy(false);
  refreshDrawing();
});

watch(isMarked, (marked: boolean) => {  
  const currentState = getCurrentSlideState();
  if (isReviewState(currentState)) {
    localCellTag.value = marked ? SELECTED_TAG : originCellTag.value;
    return;
  }
  localCellTag.value = marked ? UNCLASSIFIED_TAG : '';
});

watch(effectiveCellTag, (cellTag: string | undefined) => {
  if (cellTag !== localCellTag.value) {
    originCellTag.value = cellTag || '';
    localCellTag.value = cellTag || '';
    try {
      const currentState = getCurrentSlideState();
      isMarked.value = originCellTag.value !== '' && !isReviewState(currentState);
    } catch (e) {
      console.error(e);
      isMarked.value = false;
    }
  }
});

watch(() => localCellTag.value, (cellTag: string) => {
  try {
    cellGroups.tagCell({
      fromType: props.cellType,
      tileId: props.tileId,
      imageUrl: props.imgPath,
      tag: cellTag,
    });
  } catch (e) { console.error(e); }
});

watch(shouldRenderStage, (shouldRender: boolean) => {
  if (shouldRender) {
    refreshDrawing();
  } else {
    destroyStage();
  }
}, { immediate: true });

watch(effectiveBBoxes, () => {
  refreshDrawing();
}, { deep: true, immediate: true });

function imgError() {
  if (loadRetry.value > 3) return;
  loadRetry.value += 1;
  if (cachedImgPath) {
    imgSrc.value = $api.getCellUrls(cachedImgPath);
  }
}

function clearTouchClickSuppression() {
  suppressNextTouchClick = false;
}

function isProspectiveRulerHit(event: PointerEvent): boolean {
  if (!rulerStore.isVisible(props.taskId)) return false;
  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) return false;
  const layout = measurementLayout.value;
  if (!layout) return false;
  const bounds = target.getBoundingClientRect();
  const localX = event.clientX - bounds.left;
  const localY = event.clientY - bounds.top;
  const hitLeft = layout.left + layout.hitBounds.left;
  const hitTop = layout.top + layout.hitBounds.top;
  return (
    localX >= hitLeft
    && localX <= hitLeft + layout.hitBounds.width
    && localY >= hitTop
    && localY <= hitTop + layout.hitBounds.height
  );
}

function changeMark(event?: MouseEvent) {
  if (suppressNextTouchClick) {
    const pointerType = (event as PointerEvent | undefined)?.pointerType;
    if (!pointerType || pointerType === "touch") {
      clearTouchClickSuppression();
      return;
    }
  }
  if (config.isReadOnly) return;
  if (!props.drawingMode) {
    isMarked.value = !isMarked.value;
  }
  emit('activate');
}

function activateRulerSurface(event?: PointerEvent | FocusEvent) {
  if (!props.taskId || !showThisCell.value || !isMeasurementSurfaceIntersecting.value) return;
  const wasActive = rulerStore.isActiveSurface(props.taskId, rulerSurfaceId.value);
  if (wasActive) return;
  if (event && "pointerType" in event && event.pointerType === "touch") {
    clearTouchClickSuppression();
    suppressNextTouchClick = isProspectiveRulerHit(event);
  }
  rulerStore.activateSurface(props.taskId, rulerSurfaceId.value);
}

function getImage(imgSrcLocal: string) {
  return new Promise<HTMLImageElement>((resolve) => {
    const img = new Image();
    img.src = imgSrcLocal;
    img.onload = () => resolve(img);
  });
}

async function getRawImgSize(
  expectedRequestId = imgRequestId,
  expectedSource = imgSrc.value,
) {
  if (rawImgSize.value !== CELL_DEFAULT_SIZE) return;
  if (!expectedSource) return;
  const img = await getImage(expectedSource);
  if (
    expectedRequestId !== imgRequestId
    || expectedSource !== imgSrc.value
  ) return;
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const width = naturalWidth || img.width || CELL_DEFAULT_SIZE;
  rawImgSize.value = width;
  measurementNaturalWidth.value = naturalWidth > 0 ? naturalWidth : 0;
  measurementNaturalHeight.value = naturalHeight > 0 ? naturalHeight : 0;
}

function handleImageLoad(event: Event) {
  const target = event.target as HTMLImageElement | null;
  const boundSource = target?.getAttribute?.('src');
  if (boundSource && boundSource !== imgSrc.value) return;
  if (
    props.imgPath
    && (!pendingImagePath.value || pendingImagePath.value !== props.imgPath)
  ) return;
  if (target?.naturalWidth && target.naturalHeight) {
    rawImgSize.value = target.naturalWidth;
    measurementNaturalWidth.value = target.naturalWidth;
    measurementNaturalHeight.value = target.naturalHeight;
    displayedImagePath.value = pendingImagePath.value || props.imgPath || '';
  }
  if (!isVisible.value) {
    isVisible.value = true;
  }
  refreshDrawing();
}

function handleRightClick(event: MouseEvent) {
  if (config.isReadOnly) return;
  emit('cellRightClick', event, props.cellType, props.tileId, props.imgPath);
}

function getPointerPos() {
  if (!stage) return null;
  const pos = stage.getPointerPosition();
  return pos ? { x: pos.x, y: pos.y } : null;
}

function handleMouseDownKonva() {
  emit('activate');
  const pos = getPointerPos();
  if (!pos) return;
  if (!props.drawingMode || config.isReadOnly) return;
  isDragging.value = true;
  const rawStart = rawBoxFromStage(
    [pos.x, pos.y, pos.x + 1, pos.y + 1],
    bboxScaleX.value,
    bboxScaleY.value,
    bboxNaturalWidth.value,
    bboxNaturalHeight.value,
  );
  if (rawStart) {
    customBbox.value = [rawStart[0], rawStart[1], rawStart[0] + 1, rawStart[1] + 1];
  }
  // Add global mouseup listener to handle clicks outside the image
  window.addEventListener('mouseup', handleMouseUpKonva);
  draw();
}

function handleMouseMoveKonva() {
  if (!isDragging.value || customBbox.value.length < 4) return;
  if (!props.drawingMode || config.isReadOnly) return;
  const pos = getPointerPos();
  if (!pos) return;
  const rawEnd = rawBoxFromStage(
    [pos.x, pos.y, pos.x + 1, pos.y + 1],
    bboxScaleX.value,
    bboxScaleY.value,
    bboxNaturalWidth.value,
    bboxNaturalHeight.value,
  );
  if (rawEnd) {
    customBbox.value = [customBbox.value[0], customBbox.value[1], rawEnd[0], rawEnd[1]] as BBox;
  }
  draw();
}

function handleMouseUpKonva() {
  if (!isDragging.value || !props.drawingMode || config.isReadOnly) return;
  isDragging.value = false;
  
  // customBbox already stores raw coordinates, save directly
  if (customBbox.value.length >= 4) {
    cellGroups.changeBbox({
      fromType: props.cellType,
      tileId: props.tileId,
      imageUrl: props.imgPath,
      bboxes: [customBbox.value as BBox],
    });
  }   
  
  // Clear draft bbox immediately after saving
  customBbox.value = [];
  // Remove global mouseup listener
  window.removeEventListener('mouseup', handleMouseUpKonva);
  refreshDrawing();
}

// removed unused changeToUnclassfied

function handleMouseOver() {
  isMouseOver.value = true;
}

function handleMouseOut() {
  isMouseOver.value = false;
}

function clearLayer() {
  if (!layer) return;
  layer.destroyChildren();
}

function draw() {
  if (!layer) return;
  clearLayer();
  // draw server bboxes
  allRectConfigs.value.forEach((cfg) => {
    const rect = new Konva.Rect({
      x: cfg.x,
      y: cfg.y,
      width: cfg.width,
      height: cfg.height,
      stroke: cfg.stroke,
      strokeWidth: cfg.strokeWidth,
    });
    layer?.add(rect);
  });
  // draw custom bbox
  const ccfg = customRectConfig.value;
  if (ccfg) {
    const rect = new Konva.Rect({
      x: ccfg.x,
      y: ccfg.y,
      width: ccfg.width,
      height: ccfg.height,
      stroke: ccfg.stroke,
      strokeWidth: ccfg.strokeWidth,
    });
    layer.add(rect);
  }
  layer.draw();
}

function syncStageDimensions() {
  if (!stage) return;
  stage.width(thisImgWidth.value);
  stage.height(thisImgWidth.value);
  layer?.draw();
}

function createStage() {
  if (stage || !konvaRoot.value) return;
  stage = new Konva.Stage({ container: konvaRoot.value, width: thisImgWidth.value, height: thisImgWidth.value });
  layer = new Konva.Layer();
  stage.add(layer);
  stage.on('mousedown', handleMouseDownKonva);
  stage.on('mousemove', handleMouseMoveKonva);
  stage.on('mouseup', handleMouseUpKonva);
  draw();
}

function destroyStage() {
  if (!stage) return;
  stage.off('mousedown', handleMouseDownKonva);
  stage.off('mousemove', handleMouseMoveKonva);
  stage.off('mouseup', handleMouseUpKonva);
  stage.destroy();
  stage = null;
  layer = null;
}

const interactionHandler = useIntersectionHandler(
  rawImgSize,
  handleVisibilityChange,
  getRawImgSize,
  refreshDrawing,
  CELL_DEFAULT_SIZE
);

function handleMeasurementSurfaceIntersection(entries: IntersectionObserverEntry[]) {
  const entry = entries[0];
  isMeasurementSurfaceIntersecting.value = Boolean(entry?.isIntersecting);
  if (entry) interactionHandler(entries);
}

onMounted(async () => {
  if (props.imgPath && props.imgPath !== cachedImgPath) {
    const requestId = await loadImageForPath(props.imgPath, { resetRawSize: false });
    if (requestId === imgRequestId) {
      void getRawImgSize(requestId, imgSrc.value);
    }
  }
  baseImgWidth.value = props.imgWidth;

  // Initialize local tag from effective (store-backed) tag if available
  const initTag = effectiveCellTag.value !== SELECTED_TAG ? effectiveCellTag.value || '' : UNCLASSIFIED_TAG;
  localCellTag.value = initTag;
  originCellTag.value = initTag;

  const currentState = getCurrentSlideState();
  isMarked.value = originCellTag.value !== '' && !isReviewState(currentState);

  // Setup intersection observer for visibility detection
  const { stop } = useIntersectionObserver(
    imgElement,
    handleMeasurementSurfaceIntersection,
  );
  
  // Store the stop function to cleanup on unmount
  stopIntersectionObserver = stop;
});

onBeforeUnmount(() => {
  imgRequestId += 1;
  if (widthUpdateFrame.value) {
    cancelAnimationFrame(widthUpdateFrame.value);
    widthUpdateFrame.value = null;
  }
  // Clean up intersection observer
  if (stopIntersectionObserver) {
    stopIntersectionObserver();
    stopIntersectionObserver = null;
  }
  // Remove global mouseup listener if still attached
  window.removeEventListener('mouseup', handleMouseUpKonva);
  clearTouchClickSuppression();
  destroyStage();
});
</script>
<style lang="scss" scoped>
@use "@/assets/scss/common/alignment" as *;
.canvas-wrapper {
  margin: 20px;
  position: relative;
  overflow: hidden;
  &.canvas-wrapper--measurement-ruler-overflow {
    overflow: visible;
  }
  &.unclassified {
    box-shadow: 0px 0px 10px 0px rgba(255, 0, 0, 0.836);
  }
  &.selected {
    box-shadow: 0px 0px 10px 0px rgb(0, 140, 255);
  }
  &:hover {
    cursor:pointer
  }
}
.canvas-overlay{
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.canvas-wrapper > img {
  display: block;
}
.tile-info{
  text-align: center;
  border-bottom: 2px solid #00000059;
  padding-bottom: 3px;
}
</style>
